import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '@cx-web/components/primitives/icon';
import { useBetslipContext } from '@cx-web/feature-betslip/context';
import { getOddsByFormat } from '@cx-web/feature-odds-format';
import useMedia from '@cx-web/ui-shell/use-media';
import { IconPadlock } from '@fpsm/cxy-icons';

import { type Repub, type RepubConfig, REPUBS } from '@cx-modules/constants';
import { type BookmakersConfig } from '@cx-modules/types';

import { BetButton } from '~/apps/product-plus/components/bet-button/BetButton.tsx';
import {
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Tooltip,
} from '~/apps/product-plus/components/cxy/cxy.ts';
import { type PremiumMarketMoversResult } from '~/apps/product-plus/configuration/types';
import { formatDate } from '~/apps/product-plus/configuration/utils/formatDate';
import { useCurrentPath } from '~/apps/product-plus/hooks/use-current-path';

import { trackMarketMoversBetClick } from '../tracking';

import styles from './styles/MarketMoversCard.module.css';

export interface MarketMoversCardProps {
  bookmakers: BookmakersConfig;
  content: PremiumMarketMoversResult;
  isLocked?: boolean;
  repub: Repub;
  repubPath: string;
  resourcesUrl: string;
}

interface LockedContentProps {
  className?: string;
  currentPath: string;
  message: string;
  repubPath?: string;
}

interface SwingDisplayProps {
  isLocked: boolean;
  isMobile?: boolean;
  swingPercentage?: number;
}

interface HorseInfoProps {
  betName?: string;
  currentPath: string;
  isLocked: boolean;
  jockey?: string;
  jockeyColours?: { description: string; fileName: string };
  repubPath: string;
}

const SwingTooltip = () => (
  <Tooltip delayDuration={400}>
    <Tooltip.Trigger>
      <Icon icon="Info" />
    </Tooltip.Trigger>
    <Tooltip.Content side="top" sideOffset={15}>
      Price swing shows the % difference between the best starting odds and the current best odds for each selection
    </Tooltip.Content>
  </Tooltip>
);

const LockedContent = ({ className, currentPath, message, repubPath }: LockedContentProps) => (
  <div className={`${styles.horseInfoLocked} ${className || ''}`}>
    <IconPadlock className={styles.padlock} />
    <Button asChild className={styles.ghostButton} variant="ghost">
      <a href={`${repubPath}/plus?redirectTo=${currentPath}#plans`}>Subscribe</a>
    </Button>
    {message}
  </div>
);

const SwingDisplay = ({ isLocked, isMobile = false, swingPercentage = 0 }: SwingDisplayProps) => {
  const isPositiveSwing = swingPercentage <= 0;

  const containerClass = isMobile ? styles.mobileSwingCarouselItem : styles.desktopSwingContainer;

  const swingClasses = `${styles.swingTriangle} ${isPositiveSwing ? styles.positive : styles.negative}`;
  const percentageClasses = `${styles.swingPercentage} ${isPositiveSwing ? styles.positive : styles.negative}`;
  const progressClasses = `${styles.swingProgressFill} ${isPositiveSwing ? styles.positive : styles.negative}`;

  return (
    <div className={containerClass}>
      {isMobile && (
        <div className={styles.swingHeader}>
          <span className={styles.swingLabel}>Swing</span>
          <SwingTooltip />
        </div>
      )}
      <div className={styles.swingValueContainer} data-testid="swing-percentage">
        {isLocked ? (
          <IconPadlock className={styles.padlock} />
        ) : (
          <>
            <span className={swingClasses}>{isPositiveSwing ? '▼' : '▲'}</span>
            <span className={percentageClasses}>{Math.abs(swingPercentage)}%</span>
          </>
        )}
      </div>
      <div className={styles.swingProgressBar}>
        <div
          className={progressClasses}
          data-testid="swing-bar"
          style={{ '--progress-width': `100%` } as React.CSSProperties}
        />
      </div>
    </div>
  );
};

const HorseInfo = ({ betName, currentPath, isLocked, jockey, jockeyColours, repubPath }: HorseInfoProps) => {
  if (isLocked) {
    return <LockedContent currentPath={currentPath} message=" to unlock pick" repubPath={repubPath} />;
  }

  return (
    <div className={styles.horseInfo} data-testid="horse-info">
      <div className={styles.horseAvatar}>
        <img alt={jockeyColours?.description} className={styles.avatarImage} src={jockeyColours?.fileName} />
      </div>
      <div className={styles.horseDetails}>
        <h3 className={styles.horseName}>{betName}</h3>
        <p className={styles.jockeyName}>{jockey}</p>
      </div>
    </div>
  );
};

const buildEventTitle = (eventDisplayName: string, subeventStartTime: string, repubConfig: RepubConfig): string => {
  const timeFormat = formatDate(subeventStartTime, 'HH:mm', repubConfig);
  const dateFormat = formatDate(subeventStartTime, 'EEE, MMM d', repubConfig);

  return `UK & Ireland | ${eventDisplayName} ${timeFormat} | ${dateFormat}`;
};

export const MarketMoversCard = ({
  bookmakers,
  content,
  isLocked = false,
  repub,
  repubPath,
}: MarketMoversCardProps) => {
  const { t } = useTranslation();
  const { horseRacingSportsData, marketMovement, marketName } = content;
  const { userOddsFormat } = useBetslipContext();
  const { isDesktop } = useMedia();
  const currentPath = useCurrentPath();

  const repubConfig = REPUBS[repub];
  const bookmaker = bookmakers[content.bookmakerCode];
  const swingPercentage = marketMovement?.swingPercentage ?? 0;

  const bestStartPrice = marketMovement
    ? getOddsByFormat(
        {
          oddsDecimal: marketMovement.bestStartPrice,
          oddsFractional: marketMovement.bestStartPriceFractional,
          oddsAmerican: marketMovement.bestStartPriceAmerican,
        },
        userOddsFormat,
      )
    : null;

  const shortestLiveOdds = content.shortestOdds
    ? getOddsByFormat(
        {
          oddsDecimal: content.shortestOdds.oddsDecimal,
          oddsFractional: content.shortestOdds.oddsFractional,
          oddsAmerican: content.shortestOdds.oddsAmerican,
        },
        userOddsFormat,
      )
    : null;

  const handleBetClick = useCallback(() => {
    if (!marketMovement || !horseRacingSportsData || !bestStartPrice) return;

    trackMarketMoversBetClick({ bestStartPrice, bookmaker, result: content });
  }, [bestStartPrice, bookmaker, content, horseRacingSportsData, marketMovement]);

  if (!marketName || !horseRacingSportsData || !marketMovement) {
    return null;
  }

  const eventTitle = buildEventTitle(content.eventDisplayName, content.subeventStartTime, repubConfig);

  if (!isDesktop) {
    return (
      <div className={styles.marketMoversCard}>
        <div className={styles.eventHeader}>
          <div className={styles.eventInfo} data-testid="event-title">
            {eventTitle}
          </div>
        </div>

        <div className={styles.horseInfoSection}>
          <HorseInfo
            betName={content.betName}
            currentPath={currentPath}
            isLocked={isLocked}
            jockey={horseRacingSportsData.jockey}
            jockeyColours={horseRacingSportsData.jockeyColours}
            repubPath={repubPath}
          />
        </div>

        <div className={styles.mobileCarouselContainer}>
          <Carousel controlsPosition="inside">
            <CarouselContent className={styles.carouselContent}>
              <CarouselItem className={styles.carouselItemSmall}>
                <div className={styles.mobileCarouselItem}>
                  <span className={styles.mobileCarouselItemLabel}>Best Original Odds</span>
                  <span className={styles.mobileCarouselItemValue} data-testid="best-orig-odds">
                    {isLocked ? <IconPadlock className={styles.padlock} /> : bestStartPrice}
                  </span>
                </div>
              </CarouselItem>

              <CarouselItem className={styles.carouselItemSmall}>
                <div className={styles.mobileCarouselItem}>
                  <span className={styles.mobileCarouselItemLabel}>Shortest Live Odds</span>
                  <span className={styles.mobileCarouselItemValue} data-testid="shortest-odds">
                    {isLocked ? <IconPadlock className={styles.padlock} /> : shortestLiveOdds}
                  </span>
                </div>
              </CarouselItem>

              <CarouselItem className={styles.carouselItemMedium}>
                <SwingDisplay isLocked={isLocked} isMobile={true} swingPercentage={swingPercentage} />
              </CarouselItem>

              <CarouselItem className={styles.carouselItemSmall}>
                <div className={styles.mobileCarouselItem}>
                  <span className={styles.mobileCarouselItemLabel}>Bookies with Odds</span>
                  <span className={styles.mobileCarouselItemValue} data-testid="bookies-with-odds">
                    {isLocked ? <IconPadlock className={styles.padlock} /> : marketMovement.totalBookmakersPriced}
                  </span>
                </div>
              </CarouselItem>

              <CarouselItem className={styles.carouselItemSmall}>
                <div className={styles.mobileCarouselItem}>
                  <span className={styles.mobileCarouselItemLabel}>Bookies with Odds Moved</span>
                  <span className={styles.mobileCarouselItemValue} data-testid="bookies-odds-moved">
                    {isLocked ? <IconPadlock className={styles.padlock} /> : marketMovement.totalBookmakersMoved}
                  </span>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious className={styles.carouselControl} />
            <CarouselNext className={styles.carouselControl} />
          </Carousel>
        </div>
        <div className={styles.mobileBetButtonContainer}>
          <BetButton
            bet={content}
            bookieColour={bookmaker.brandColour || 'black'}
            bookieLogo={bookmaker.logoSmall}
            bookieName={bookmaker.bookmakerName}
            locked={isLocked}
            onClick={handleBetClick}
            unformattedOdds={content}
          />
        </div>
        {isLocked ? (
          <div className={styles.horseStatsSection}>
            <LockedContent
              className={styles.lockedAnalysisCenter}
              currentPath={currentPath}
              message=" to unlock analysis"
              repubPath={repubPath}
            />
          </div>
        ) : (
          <>
            <div className={styles.horseStatsSection}>
              <div className={styles.horseStatsGrid}>
                <div className={styles.statItem} data-testid="form">
                  <span className={styles.statLabel}>Form:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.formFigures}</span>
                </div>
                <div className={styles.statItem} data-testid="age">
                  <span className={styles.statLabel}>Age:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.age}</span>
                </div>
                <div className={styles.statItem} data-testid="trainer">
                  <span className={styles.statLabel}>T:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.trainer}</span>
                </div>
                <div className={styles.statItem} data-testid="weight">
                  <span className={styles.statLabel}>Weight:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.weight}</span>
                </div>
                <div className={styles.statItem} data-testid="owner">
                  <span className={styles.statLabel}>O:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.owner}</span>
                </div>
              </div>
            </div>
            <div className={styles.description} data-testid="rationale">
              {marketMovement.rationaleText}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.marketMoversCard}>
      <div className={styles.headerRow}>
        <div className={styles.eventTitle} data-testid="event-title">
          {eventTitle}
        </div>
        <div className={styles.headerColumns}>
          <div className={styles.headerColumn}>
            <span className={styles.columnHeading}>Best Orig. Odds</span>
          </div>
          <div className={styles.headerColumn}>
            <span className={styles.columnHeading}>Shortest Live Odds</span>
          </div>
          <div className={styles.headerColumn}>
            <span className={styles.columnHeading}>Swing</span>
            <SwingTooltip />
          </div>
          <div className={styles.headerColumn}>
            <span className={styles.columnHeading}>Bookies with Odds</span>
          </div>
          <div className={styles.headerColumn}>
            <span className={styles.columnHeading}>Bookies with Odds Moved</span>
          </div>
          <div className={styles.headerColumn}>
            <span className={styles.columnHeading}>{t('BEST_ODDS')}</span>
          </div>
        </div>
      </div>

      <div className={styles.contentRow}>
        <div className={styles.horseInfoColumn}>
          <HorseInfo
            betName={content.betName}
            currentPath={currentPath}
            isLocked={isLocked}
            jockey={horseRacingSportsData.jockey}
            jockeyColours={horseRacingSportsData.jockeyColours}
            repubPath={repubPath}
          />
        </div>
        <div className={styles.dataColumns}>
          <div className={styles.dataColumn}>
            <span className={styles.oddsValue} data-testid="best-orig-odds">
              {isLocked ? <IconPadlock className={styles.padlock} /> : bestStartPrice}
            </span>
          </div>
          <div className={styles.dataColumn}>
            <span className={styles.oddsValue} data-testid="shortest-odds">
              {isLocked ? <IconPadlock className={styles.padlock} /> : shortestLiveOdds}
            </span>
          </div>
          <div className={styles.dataColumn}>
            <SwingDisplay isLocked={isLocked} isMobile={false} swingPercentage={swingPercentage} />
          </div>
          <div className={styles.dataColumn}>
            <span className={styles.oddsValue} data-testid="bookies-with-odds">
              {isLocked ? <IconPadlock className={styles.padlock} /> : marketMovement.totalBookmakersPriced}
            </span>
          </div>
          <div className={styles.dataColumn}>
            <span className={styles.oddsValue} data-testid="bookies-odds-moved">
              {isLocked ? <IconPadlock className={styles.padlock} /> : marketMovement.totalBookmakersMoved}
            </span>
          </div>
          <div className={styles.dataColumn}>
            <div className={styles.bestOddsContainer}>
              <BetButton
                bet={content}
                bookieColour={bookmaker.brandColour || 'black'}
                bookieLogo={bookmaker.logoSmall}
                bookieName={bookmaker.bookmakerName}
                locked={isLocked}
                onClick={handleBetClick}
                shouldHideLabelOnDesktop
                shouldHideLabelOnMobile
                unformattedOdds={content}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.statsAndDescription}>
        {isLocked ? (
          <LockedContent
            className={styles.lockedAnalysisDesktop}
            currentPath={currentPath}
            message=" to unlock analysis"
            repubPath={repubPath}
          />
        ) : (
          <>
            <div className={styles.horseStatsRow}>
              <div className={styles.statsColumns}>
                <div className={styles.statItem} data-testid="form">
                  <span className={styles.statLabel}>Form:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.formFigures}</span>
                </div>
                <div className={styles.statItem} data-testid="age">
                  <span className={styles.statLabel}>Age:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.age}</span>
                </div>
                <div className={styles.statItem} data-testid="trainer">
                  <span className={styles.statLabel}>T:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.trainer}</span>
                </div>
                <div className={styles.statItem} data-testid="weight">
                  <span className={styles.statLabel}>Weight:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.weight}</span>
                </div>
                <div className={styles.statItem} data-testid="owner">
                  <span className={styles.statLabel}>O:</span>
                  <span className={styles.statValue}>{horseRacingSportsData.owner}</span>
                </div>
              </div>
            </div>

            <div className={styles.description} data-testid="rationale">
              {marketMovement.rationaleText}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketMoversCard;
